import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HomeIcon, MapIcon, UploadIcon, XIcon, PlusIcon, FileTextIcon, AlertTriangleIcon } from 'lucide-react';
import { CheckCircleIcon, ClockIcon } from 'lucide-react';
import { currentUser, isLicenseExpired } from '../utils/mockData';
const CreateListingPage = () => {
  const navigate = useNavigate();
  const [listingType, setListingType] = useState<'house' | 'land'>('house');
  const [saleOrRent, setSaleOrRent] = useState<'sale' | 'rent'>('sale');
  const [rentPeriod, setRentPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [images, setImages] = useState<string[]>([]);
  const [documents, setDocuments] = useState<{
    name: string;
    size: string;
  }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Check license status
  const hasLicense = currentUser.license !== null && currentUser.license !== undefined;
  const licenseExpired = hasLicense ? isLicenseExpired(currentUser.license.expiryDate) : false;
  const hasAvailableListings = hasLicense && !licenseExpired ? currentUser.license.usedListings < currentUser.license.maxListings : false;
  const canCreateListing = hasLicense && !licenseExpired && hasAvailableListings;
  const handleListingTypeChange = (type: 'house' | 'land') => {
    setListingType(type);
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newImages = Array.from(e.target.files).map(file => URL.createObjectURL(file));
      setImages(prev => [...prev, ...newImages]);
    }
  };
  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newDocuments = Array.from(e.target.files).map(file => ({
        name: file.name,
        size: formatFileSize(file.size)
      }));
      setDocuments(prev => [...prev, ...newDocuments]);
    }
  };
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / 1048576).toFixed(1) + ' MB';
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateListing) {
      return;
    }
    if (images.length < 4) {
      alert('Please upload at least 4 images of your property.');
      return;
    }
    if (documents.length < 1) {
      alert('Please upload at least one ownership document.');
      return;
    }
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
    }, 1500);
  };
  // Show license warning if can't create listing
  if (!canCreateListing) {
    return <div className="bg-gray-50 min-h-screen w-full" data-id="element-537">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-538">
          <div className="bg-white rounded-lg shadow-md p-8" data-id="element-539">
            <div className="text-center" data-id="element-540">
              <AlertTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" data-id="element-541" />
              {!hasLicense && <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-542">
                    License Required
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-543">
                    You need an active license to create property listings.
                    Choose a plan that fits your needs.
                  </p>
                </>}
              {hasLicense && licenseExpired && <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-544">
                    License Expired
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-545">
                    Your license has expired. Renew your license to continue
                    posting property listings.
                  </p>
                </>}
              {hasLicense && !licenseExpired && !hasAvailableListings && <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-546">
                    Listing Limit Reached
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-547">
                    You've used all {currentUser.license.maxListings} listings
                    in your current plan. Upgrade to post more properties.
                  </p>
                </>}
              <div className="flex justify-center space-x-4" data-id="element-548">
                <Link to="/license-plans" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-md transition duration-200" data-id="element-549">
                  {hasLicense && !licenseExpired ? 'Upgrade Plan' : 'View License Plans'}
                </Link>
                <Link to="/dashboard" className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium px-6 py-3 rounded-md transition duration-200" data-id="element-550">
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  if (isSuccess) {
    return <div className="bg-gray-50 min-h-screen w-full" data-id="element-551">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-552">
          <div className="bg-white rounded-lg shadow-md p-8 text-center" data-id="element-553">
            <div className="h-16 w-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-554">
              <ClockIcon className="h-8 w-8 text-primary" data-id="element-555" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mt-4 mb-2" data-id="element-556">
              Listing Submitted for Review
            </h2>
            <p className="text-gray-600 mb-2" data-id="element-557">
              Your property listing has been submitted and is currently under
              review by our desk support team.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-6" data-id="element-558">
              <p className="text-sm text-blue-900" data-id="element-559">
                <strong data-id="element-560">What happens next?</strong>
                <br data-id="element-561" />
                Our team will review your listing within 24-48 hours. You'll
                receive an email notification once your listing is approved and
                live on the platform.
              </p>
            </div>
            {hasLicense && <p className="text-sm text-gray-500 mb-6" data-id="element-562">
                You have{' '}
                {currentUser.license.maxListings - currentUser.license.usedListings - 1}{' '}
                listing
                {currentUser.license.maxListings - currentUser.license.usedListings - 1 !== 1 ? 's' : ''}{' '}
                remaining in your plan.
              </p>}
            <div className="flex justify-center space-x-4" data-id="element-563">
              <Link to="/dashboard" className="bg-primary hover:bg-[rgb(var(--color-primary-dark))] text-white font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-564">
                Go to Dashboard
              </Link>
              <Link to="/browse" className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-565">
                Browse Listings
              </Link>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-566">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-567">
        <div className="mb-8" data-id="element-568">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-569">
            Create New Listing
          </h1>
          <p className="mt-2 text-gray-600" data-id="element-570">
            Fill out the form below to list your property for sale or rent.
          </p>
          {/* License Status Banner */}
          {hasLicense && <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-571">
              <p className="text-sm text-blue-900" data-id="element-572">
                <span className="font-medium" data-id="element-573">
                  {currentUser.license.maxListings - currentUser.license.usedListings}{' '}
                  listing
                  {currentUser.license.maxListings - currentUser.license.usedListings !== 1 ? 's' : ''}{' '}
                  remaining
                </span>{' '}
                in your current plan.{' '}
                <Link to="/license-plans" className="underline hover:text-blue-700" data-id="element-574">
                  Upgrade
                </Link>
              </p>
            </div>}
        </div>
        <form onSubmit={handleSubmit} className="space-y-8" data-id="element-575">
          {/* Listing Type: Sale or Rent */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-576">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-577">
              Listing Type
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-578">
              <button type="button" onClick={() => setSaleOrRent('sale')} className={`flex flex-col items-center justify-center border-2 rounded-lg p-6 transition-colors ${saleOrRent === 'sale' ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-579">
                <div className={`text-2xl mb-2 ${saleOrRent === 'sale' ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-580">
                  💰
                </div>
                <span className={`font-medium ${saleOrRent === 'sale' ? 'text-purple-600' : 'text-gray-700'}`} data-id="element-581">
                  For Sale
                </span>
              </button>
              <button type="button" onClick={() => setSaleOrRent('rent')} className={`flex flex-col items-center justify-center border-2 rounded-lg p-6 transition-colors ${saleOrRent === 'rent' ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-582">
                <div className={`text-2xl mb-2 ${saleOrRent === 'rent' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-583">
                  🏠
                </div>
                <span className={`font-medium ${saleOrRent === 'rent' ? 'text-green-600' : 'text-gray-700'}`} data-id="element-584">
                  For Rent
                </span>
              </button>
            </div>
            {saleOrRent === 'rent' && <div data-id="element-585">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-586">
                  Rent Period
                </label>
                <div className="grid grid-cols-2 gap-4" data-id="element-587">
                  <button type="button" onClick={() => setRentPeriod('monthly')} className={`px-4 py-2 border-2 rounded-md ${rentPeriod === 'monthly' ? 'border-green-600 bg-green-50 text-green-600' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-588">
                    Monthly
                  </button>
                  <button type="button" onClick={() => setRentPeriod('yearly')} className={`px-4 py-2 border-2 rounded-md ${rentPeriod === 'yearly' ? 'border-green-600 bg-green-50 text-green-600' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-589">
                    Yearly
                  </button>
                </div>
              </div>}
          </div>
          {/* Property Type Selection */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-590">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-591">
              Property Type
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-592">
              <button type="button" onClick={() => handleListingTypeChange('house')} className={`flex flex-col items-center justify-center border-2 rounded-lg p-6 transition-colors ${listingType === 'house' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-593">
                <HomeIcon className={`h-12 w-12 mb-3 ${listingType === 'house' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-594" />
                <span className={`font-medium ${listingType === 'house' ? 'text-blue-600' : 'text-gray-700'}`} data-id="element-595">
                  House
                </span>
              </button>
              <button type="button" onClick={() => handleListingTypeChange('land')} className={`flex flex-col items-center justify-center border-2 rounded-lg p-6 transition-colors ${listingType === 'land' ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-596">
                <MapIcon className={`h-12 w-12 mb-3 ${listingType === 'land' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-597" />
                <span className={`font-medium ${listingType === 'land' ? 'text-blue-600' : 'text-gray-700'}`} data-id="element-598">
                  Land
                </span>
              </button>
            </div>
          </div>
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-599">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-600">
              Basic Information
            </h2>
            <div className="space-y-4" data-id="element-601">
              <div data-id="element-602">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-603">
                  Property Title*
                </label>
                <input type="text" id="title" name="title" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Modern Family Home, Commercial Land Parcel" data-id="element-604" />
              </div>
              <div data-id="element-605">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-606">
                  Description*
                </label>
                <textarea id="description" name="description" rows={5} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Describe your property in detail..." data-id="element-607" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-608">
                <div data-id="element-609">
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-610">
                    {saleOrRent === 'rent' ? `Rent ($ per ${rentPeriod === 'monthly' ? 'month' : 'year'})*` : 'Price ($)*'}
                  </label>
                  <input type="number" id="price" name="price" min="1" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={saleOrRent === 'rent' ? 'e.g., 2500' : 'e.g., 250000'} data-id="element-611" />
                </div>
                <div data-id="element-612">
                  <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-613">
                    Size (sq ft)*
                  </label>
                  <input type="number" id="size" name="size" min="1" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., 2500" data-id="element-614" />
                </div>
              </div>
              <div data-id="element-615">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-616">
                  Location*
                </label>
                <input type="text" id="location" name="location" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Full address of the property" data-id="element-617" />
              </div>
              {listingType === 'house' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-618">
                  <div data-id="element-619">
                    <label htmlFor="bedrooms" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-620">
                      Bedrooms*
                    </label>
                    <input type="number" id="bedrooms" name="bedrooms" min="0" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-621" />
                  </div>
                  <div data-id="element-622">
                    <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-623">
                      Bathrooms*
                    </label>
                    <input type="number" id="bathrooms" name="bathrooms" min="0" step="0.5" required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-624" />
                  </div>
                </div>}
            </div>
          </div>
          {/* Property Images */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-625">
            <div className="flex items-center justify-between mb-4" data-id="element-626">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-627">
                Property Images*
              </h2>
              <span className="text-sm text-gray-500" data-id="element-628">
                {images.length}/4 minimum required
              </span>
            </div>
            <div className="mb-4" data-id="element-629">
              <p className="text-sm text-gray-600" data-id="element-630">
                Upload at least 4 high-quality images of your property. Include
                different angles and rooms.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4" data-id="element-631">
              {images.map((image, index) => <div key={index} className="relative aspect-square" data-id="element-632">
                  <img src={image} alt={`Property image ${index + 1}`} className="w-full h-full object-cover rounded-lg" data-id="element-633" />
                  <button type="button" onClick={() => removeImage(index)} className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-md hover:bg-gray-100" data-id="element-634">
                    <XIcon className="h-4 w-4 text-gray-600" data-id="element-635" />
                  </button>
                </div>)}
              {images.length < 8 && <label className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 aspect-square" data-id="element-636">
                  <UploadIcon className="h-8 w-8 text-gray-400 mb-2" data-id="element-637" />
                  <span className="text-sm text-gray-600" data-id="element-638">Add Image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-id="element-639" />
                </label>}
            </div>
            {images.length < 4 && <p className="text-sm text-yellow-600" data-id="element-640">
                Please upload at least 4 images of your property.
              </p>}
          </div>
          {/* Ownership Documents */}
          <div className="bg-white rounded-lg shadow-md p-6" data-id="element-641">
            <div className="flex items-center justify-between mb-4" data-id="element-642">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-643">
                Ownership Documents*
              </h2>
              <span className="text-sm text-gray-500" data-id="element-644">
                {documents.length}/1 minimum required
              </span>
            </div>
            <div className="mb-4" data-id="element-645">
              <p className="text-sm text-gray-600" data-id="element-646">
                Upload documents proving your ownership of the property (e.g.,
                deed, title, tax records).
              </p>
            </div>
            <div className="space-y-3 mb-4" data-id="element-647">
              {documents.map((doc, index) => <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3" data-id="element-648">
                  <div className="flex items-center" data-id="element-649">
                    <FileTextIcon className="h-5 w-5 text-blue-600 mr-3" data-id="element-650" />
                    <div data-id="element-651">
                      <p className="text-sm font-medium text-gray-900" data-id="element-652">
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-653">{doc.size}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => removeDocument(index)} className="text-gray-400 hover:text-gray-600" data-id="element-654">
                    <XIcon className="h-5 w-5" data-id="element-655" />
                  </button>
                </div>)}
              <label className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center cursor-pointer hover:border-gray-400" data-id="element-656">
                <PlusIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-657" />
                <span className="text-sm text-gray-600" data-id="element-658">Add Document</span>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleDocumentUpload} className="hidden" data-id="element-659" />
              </label>
            </div>
            {documents.length < 1 && <p className="text-sm text-yellow-600" data-id="element-660">
                Please upload at least one document proving your ownership.
              </p>}
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-661">
            <Link to="/dashboard" className="mr-4 px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-662">
              Cancel
            </Link>
            <button type="submit" disabled={isSubmitting || images.length < 4 || documents.length < 1} className={`px-6 py-2 bg-blue-600 text-white rounded-md ${isSubmitting || images.length < 4 || documents.length < 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`} data-id="element-663">
              {isSubmitting ? 'Creating...' : 'Create Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default CreateListingPage;